import { ApiConstant, KeyConstant, LangConstant, SystemConstant } from "const";
import { getLabel } from "language";
import store, { BranchActions, SystemActions } from "redux-store";
import { formatPagingParams } from "sagas/saga.helper";
import { remoteApiFactory } from "services";
import { LocalAppNotificationService, getInteractor } from "services/local.service";
import { convertString2JSON, StorageUtil, toCamel } from "utils";
import { handleShowBranchReqNotice } from "./branch-request.service";
import { checkingPushNotification } from "utils/view.utils";
import { getConversation } from "./conversation.service";
import { EventSubType } from "pubsub/PubSub.const";

export const synchNotificationList = async (prefixKey, time2FetchNotification) => {
  try {
    const localTime2FetchNotification = StorageUtil.getItem(KeyConstant.KEY_TIME_2_FETCH_NOTIFICATION, prefixKey) || 0;
    const minTime2Fetch = Math.min(localTime2FetchNotification, time2FetchNotification) || 0;
    const maxTime2Fetch = Math.max(localTime2FetchNotification, time2FetchNotification) || 0;

    const fetchNotificationParams = formatPagingParams({
      sinceTime: minTime2Fetch,
      limit: 10000,
    });

    const response = await remoteApiFactory.getBranchApi(prefixKey).getNotificationList(fetchNotificationParams);
    if (response.status === ApiConstant.STT_OK) {
      const remoteNoticeList = response.data.data || [];

      for (let index = 0; index < remoteNoticeList.length; index++) {
        const notification = remoteNoticeList[index];
        const localNotification = await getInteractor(prefixKey).LocalNotificationService.get(notification.id);
        if (localNotification) continue;
        switch (notification.type) {
          case SystemConstant.NotifyType.NOTIFICATION:
            break;

          case SystemConstant.NotifyType.SMARTOTP:
            smartOtpService(prefixKey, notification);
            break;

          case SystemConstant.NotifyType.INVITE:
            const globalBranchId = SystemConstant.GLOBAL_BRANCH_ID;
            if (globalBranchId === notification.branch_id)
              store.dispatch(
                SystemActions.systemSet({
                  newNotification: toCamel(notification),
                }),
              );
            break;

          case SystemConstant.NotifyType.OWNER_NOTIFICATION:
            await ownerNotification(prefixKey, notification);
            break;

          case SystemConstant.NotifyType.BRANCH_REQUEST:
            // Handle click notification and show request form
            await handleShowBranchReqNotice(prefixKey, notification);
            break;

          default:
            break;
        }
      }
      await getInteractor(prefixKey).LocalNotificationService.save(remoteNoticeList);
      StorageUtil.setItem(KeyConstant.KEY_TIME_2_FETCH_NOTIFICATION, maxTime2Fetch, prefixKey);

      return true;
    }
  } catch (error) {
    console.error("synch notification fail: ", error);
  }

  return false;
};

export const branchInviteService = async (prefixKey, accountId, branchId) => {
  try {
    const localNotification = await getInteractor(prefixKey).LocalNotificationService.find({
      notice_to_account_id: accountId,
      branch_id: branchId,
      type: SystemConstant.NotifyType.INVITE,
    });

    if (localNotification) return true;
  } catch (error) {
    console.error("branch invite service fail: ", error);
  }

  return false;
};

export const branchRemoveService = inactiveBranchId => {
  store.dispatch(
    BranchActions.branchSet({
      inactiveBranchArray: inactiveBranchId,
    }),
  );

  return true;
};

const smartOtpService = async (prefixKey, notification) => {
  try {
    // Check created time message and confirm message need to show or not
    if (await checkingPushNotification(notification, prefixKey)) {
      LocalAppNotificationService.showNotification(getLabel(LangConstant.TXT_APP_NAME), {
        content: `Smart-OTP: ${notification.content}`,
        prefixKey,
      });
    }

    return true;
  } catch (error) {
    console.error("smart otp notification fail: ", error);
  }

  return false;
};

export const mentionsNotification = async (prefixKey, messageSourceId) => {
  try {
    const message = await getInteractor(prefixKey).LocalMessageService.findOne({ source_id: messageSourceId });
    if (message && message.id) {
      const mentionNotification = await getInteractor(prefixKey).LocalNotificationService.findOne({
        content: message.id,
      });
      if (mentionNotification) return true;
    }
  } catch (error) {
    console.error("mention notification fail: ", error);
  }

  return false;
};

const ownerNotification = async (prefixKey, notification) => {
  try {
    const optionsObj = notification.options ? convertString2JSON(notification.options, {}) : {};
    const { group_id, sub_type } = optionsObj;
    if (sub_type === EventSubType.ADD_ACCOUNT && group_id) {
      await getConversation(prefixKey, group_id);
    }

    // Check created time message and confirm message need to show or not
    if (await checkingPushNotification(notification, prefixKey)) {
      let notifyContent = notification.content.replaceAll("<b>", "");
      notifyContent = notifyContent.replaceAll("</b>", "");

      LocalAppNotificationService.showNotification(
        getLabel(LangConstant.TXT_APP_NAME),
        {
          content: notifyContent,
          prefixKey,
        },
        toCamel({ ...notification, group_id, sub_type }),
      );

      return true;
    }
  } catch (error) {
    console.error("owner notification fail: ", error);
  }

  return false;
};

export const handlePersonalNotification = async (prefixKey, notificationId) => {
  try {
    const localNotification = await getInteractor(prefixKey).LocalNotificationService.get(notificationId);
    if (false === Boolean(localNotification && localNotification.id)) {
      const localTime2FetchNotification =
        StorageUtil.getItem(KeyConstant.KEY_TIME_2_FETCH_NOTIFICATION, prefixKey) || 0;
      synchNotificationList(prefixKey, localTime2FetchNotification);
    }

    return true;
  } catch (error) {
    console.error("other notification error: ", error);
  }

  return false;
};
